// Auth0 errors that require the user to sign in again (e.g. missing refresh token,
// consent for the API audience, or expired session).
export const isRecoverableAuthError = (error) => {
  const message = String(
    error?.message || error?.error_description || error?.error || ''
  ).toLowerCase();

  return (
    error?.error === 'missing_refresh_token' ||
    error?.error === 'consent_required' ||
    error?.error === 'login_required' ||
    error?.error === 'interaction_required' ||
    message.includes('missing refresh token') ||
    message.includes('missing_refresh_token') ||
    message.includes('consent required') ||
    message.includes('login required')
  );
};

export const AUTH_RETURN_TO_STORAGE_KEY = 'dcfTeacherAuthReturnTo';

/**
 * Only allow same-origin relative return paths. Reject protocol-relative / absolute URLs.
 */
export const getSafeAuthReturnTo = (returnTo) => {
  if (typeof returnTo !== 'string') {
    return null;
  }

  const trimmed = returnTo.trim();
  // Browsers treat backslashes as path separators in special URLs, so a value
  // such as `/\\evil.example` can resolve as a cross-origin URL.
  if (
    !trimmed.startsWith('/')
    || trimmed.startsWith('//')
    || trimmed.includes('\\')
  ) {
    return null;
  }

  return trimmed;
};

const getAuthReturnTo = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

/**
 * Persist returnTo across the Auth0 round-trip. Auth0's appState can be dropped
 * on some consent/re-auth flows, which otherwise dumps the user on "/".
 */
export const stashAuthReturnTo = (returnTo) => {
  const safe = getSafeAuthReturnTo(returnTo);
  if (!safe || typeof window === 'undefined') {
    return null;
  }
  try {
    window.sessionStorage.setItem(AUTH_RETURN_TO_STORAGE_KEY, safe);
  } catch {
    // sessionStorage may be unavailable (private mode / blocked storage).
  }
  return safe;
};

export const consumeAuthReturnTo = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const value = window.sessionStorage.getItem(AUTH_RETURN_TO_STORAGE_KEY);
    window.sessionStorage.removeItem(AUTH_RETURN_TO_STORAGE_KEY);
    return getSafeAuthReturnTo(value);
  } catch {
    return null;
  }
};

export const resolvePostLoginReturnTo = (appState) => {
  const appStateReturnTo = getSafeAuthReturnTo(appState?.returnTo);
  const storedReturnTo = consumeAuthReturnTo();
  return appStateReturnTo || storedReturnTo;
};

export const getLoginRedirectOptions = (
  returnTo = getAuthReturnTo(),
  { authorizationParams: extraAuthorizationParams } = {},
) => {
  const safeReturnTo = stashAuthReturnTo(returnTo) || getSafeAuthReturnTo(returnTo) || '/';
  return {
    appState: { returnTo: safeReturnTo },
    authorizationParams: {
      redirect_uri: window.location.origin,
      ...extraAuthorizationParams,
    },
  };
};

/**
 * Auth0 redirects back to redirect_uri (usually "/") with code/state (or error/state).
 * Detect those params so we can avoid painting the destination page until returnTo is applied.
 */
export const hasAuth0RedirectCallbackParams = (search = '') => {
  const query = String(search || '');
  const hasState = /[?&]state=[^&]+/.test(query);
  if (!hasState) {
    return false;
  }
  return /[?&]code=[^&]+/.test(query) || /[?&]error=[^&]+/.test(query);
};

/**
 * Apply post-login return navigation. Uses history.replaceState first so the browser URL
 * matches returnTo before React paints, then syncs React Router synchronously so Auth0's
 * isLoading=false render does not briefly paint "/".
 */
export const applyAuthReturnTo = (navigate, returnTo, { flushSync } = {}) => {
  const target = getSafeAuthReturnTo(returnTo) || '/';
  if (typeof window !== 'undefined' && window.history?.replaceState) {
    window.history.replaceState({}, document.title, target);
  }

  const syncNavigate = () => {
    navigate(target, { replace: true });
  };

  if (typeof flushSync === 'function') {
    flushSync(syncNavigate);
  } else {
    syncNavigate();
  }

  return target;
};
